<template>
  <div class="border border-gray-400 dark:border-gray-100 rounded-lg">
    <div class="space-y-5 p-5">
      <h3 class="text-xl font-semibold text-main-color">Mes Informations</h3>

      <div v-if="isLoading" class="space-y-4">
        <USkeleton class="h-12 w-full" />
        <USkeleton class="h-12 w-full" />
        <USkeleton class="h-12 w-full" />
      </div>

      <UForm
        v-else
        :schema="schema"
        :state="state"
        class="space-y-4"
        @submit="save"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UFormField label="Nom d'utilisateur" name="username">
            <UInput
              v-model="state.username"
              disabled
              color="gray"
              variant="outline"
              size="lg"
              icon="i-lucide-user"
              class="w-full"
            />
          </UFormField>
          <UFormField label="Email" name="email">
            <UInput
              v-model="state.email"
              disabled
              color="gray"
              variant="outline"
              size="lg"
              icon="i-lucide-mail"
              class="w-full"
            />
          </UFormField>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UFormField label="Prénom" name="firstName">
            <UInput
              v-model="state.firstName"
              color="main"
              size="lg"
              class="w-full"
            />
          </UFormField>
          <UFormField label="Nom" name="lastName">
            <UInput
              v-model="state.lastName"
              color="main"
              size="lg"
              class="w-full"
            />
          </UFormField>
        </div>

        <UFormField label="Téléphone" name="phoneNumber">
          <UInput
            v-model="state.phoneNumber"
            color="main"
            size="lg"
            icon="i-lucide-phone"
            class="w-full"
          />
        </UFormField>

        <div class="flex justify-end pt-2">
          <UButton
            :loading="isSaving"
            type="submit"
            color="main"
            size="lg"
            label="Enregistrer les modifications"
          />
        </div>
      </UForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { z } from "zod";
import type { FormSubmitEvent } from "#ui/types";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { user, fetchUser, token } = useAuth();
const toast = useToast();

const schema = z.object({
  username: z.string(),
  email: z.string().email(),
  firstName: z.string().min(2, "Minimum 2 caractères"),
  lastName: z.string().min(2, "Minimum 2 caractères"),
  phoneNumber: z.string().min(10, "Numéro invalide"),
});

type Schema = z.output<typeof schema>;

const state = reactive({
  username: "",
  email: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
});

watchEffect(() => {
  if (user.value) {
    state.username = user.value.username || "";
    state.email = user.value.email || "";
    state.firstName = user.value.first_name || "";
    state.lastName = user.value.last_name || "";
    state.phoneNumber = user.value.phone_number || "";
  }
});

const isLoading = ref(true);

onMounted(async () => {
  if (!user.value) {
    try {
      await fetchUser();
    } finally {
      isLoading.value = false;
    }
  } else {
    isLoading.value = false;
  }

  if (!user.value) {
    await navigateTo("/login");
  }
});

const isSaving = ref(false);

const save = async (event: FormSubmitEvent<Schema>) => {
  isSaving.value = true;
  try {
    const { $api } = useNuxtApp();
    await $api.patch(
      "/user/me",
      {
        first_name: event.data.firstName,
        last_name: event.data.lastName,
        phone_number: event.data.phoneNumber,
      },
      "include",
    );

    // Update local user state
    if (user.value) {
      user.value.first_name = event.data.firstName;
      user.value.last_name = event.data.lastName;
      user.value.phone_number = event.data.phoneNumber;
    }

    toast.add({ title: "Profil mis à jour", color: "green" });
  } catch {
    toast.add({ title: "Erreur lors de la mise à jour", color: "red" });
  } finally {
    isSaving.value = false;
  }
};
</script>
